import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import {
  showToast,
  ThankYou,
  Modal,
  ModalTitle,
  ModalActions,
  ModalContent,
  Button,
  Typography,
} from "@deeptrue-frontend/ui";
import styled from "styled-components/macro";
import IntlProvider, { useFormatMessage } from "components/IntlProvider";
import { Dashboard } from "components/Layout";
import api from "api";
import StoreProvider from "components/StoreProvider/StoreProvider";
import { ProductsReducerType } from "store/modules/products/products.types";
import SurveyBriefing from "components/SurveyBriefing/SurveyBriefing";
import RouterProvider from "components/RouterProvider/RouterProvider";
import BexioDeeptrueIntegrationImage from "assets/images/deeptruebexio.png";
import { insightsQuery } from "api/prismic";
import Loader from "components/Loader/Loader";
import TagManager from "react-gtm-module";

const Container = styled.div`
  margin-top: 5rem;
`;

const ImageBexio = styled.img`
  max-width: calc(100% - 4rem);
  margin: 2rem;
  margin-bottom: 0;
  box-sizing: border-box;
`;

const BexioModalContent = styled(ModalContent)`
  text-align: center;
`;

const BexioModalTitle = styled(ModalTitle)`
  text-align: center;
  font-size: 1.25rem;
  line-height: 2rem;
  margin-top: 1.5rem;
`;

const { connect, selectors, actions } = StoreProvider;
const prismicLangs: any = {
  en: "en-gb",
  de: "de-de",
  fr: "fr-fr",
  it: "it-it",
};

interface BriefingProps
  extends RouteComponentProps<{ product_slug: string; codename: string }> {
  briefing: ProductsReducerType["selected"]["briefing_template"];
  setProduct: (product: ProductsReducerType["selected"]) => void;
  user: any;
  clearBriefieng: any;
  isBexioUser: boolean;
  product: any;
  isBrandValuatorProduct: boolean;
}

const Briefing = ({
  match,
  setProduct,
  briefing,
  location,
  history,
  user,
  clearBriefieng,
  isBexioUser,
  product,
  isBrandValuatorProduct,
}: BriefingProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(isBexioUser);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [insights, setInsights] = useState([]);
  const { locale } = IntlProvider.useIntl();
  const t = useFormatMessage();
  const { params } = match;
  const { slug } = product;
  const projectNamePage =
    typeof briefing.json !== "undefined" &&
    typeof briefing.json.pages !== "undefined" &&
    !isBexioUser
      ? [
          {
            name: "name",
            type: "text",
            title: t("briefing.project.name", "Project name"),
            placeHolder: t(
              "briefing.project.name.placeholder",
              "Enter the project name"
            ),
            hasTooltip: true,
            tooltip: t(
              "briefing.project.name.tooltip",
              "Naming the project will help you to recognise it among your other projects."
            ),
            isRequired: true,
          },
        ]
      : [];

  useEffect(() => {
    clearBriefieng();
    Promise.all([
      api.resources.getProduct({
        slug: params.product_slug,
      }),
      insightsQuery(prismicLangs[locale]),
    ]).then(([product, insights]) => {
      setProduct(product.data);

      const insight = insights.results.filter(
        (result: any) =>
          result.data["tool_detail.tool_django_slug"].value ===
          params.product_slug
      );

      if (insight.length > 0) {
        setInsights(insight[0].data["tool_detail.body"].value[0].repeat);
      }

      setIsDataLoaded(true);
    });

    return () => {
      clearBriefieng();
    };
    // eslint-disable-next-line
  }, []);

  const handleSubmit = ({
    name,
    Bvoucher,
    ...rest
  }: {
    [key: string]: any;
  }) => {
    setIsProcessing(true);

    return api.resources
      .createProject({
        organizationCodename: params.codename,
        project: {
          name: isBexioUser ? "Bexio Customer Satisfaction" : name,
          json: rest,
          email: user.email,
          product_slug: params.product_slug,
          language: locale,
          code: Bvoucher ? Bvoucher.code : null,
        },
      })
      .then(({ data }: any) => {
        TagManager.dataLayer({
          dataLayer: {
            event: "project_created",
            email: user.email,
            project_name: isBexioUser ? "Bexio Customer Satisfaction" : name,
            product_slug: params.product_slug,
            language: locale,
            voucher_code: Bvoucher ? Bvoucher.code : null,
            date: new Date().toLocaleString(),
          },
        });
        history.push(`/project/${data.id}/review`);
      });
  };

  if (briefing === null) {
    showToast({
      type: "error",
      title:
        "The project you want to create does not have a template. Please contact with deeptrue administrator.",
    });
    return (
      <RouterProvider.Redirect to={RouterProvider.ROUTES.PROJECTS.CREATE} />
    );
  }

  return (
    <Dashboard title={t("briefing.title", "Setup")} location={location}>
      <Grid item xs={12} style={{ marginTop: "2rem" }}>
        {isProcessing ? (
          <Container>
            <ThankYou
              title={t("briefing.thankYou.title", "Thank You!")}
              description={t(
                "briefing.thankYou.description",
                "You have completed the briefing"
              )}
              loadingTitle={t(
                "briefing.thankYou.loadingTitle",
                "Loading your project …"
              )}
              loadingContent={t(
                "briefing.thankYou.loadingContent",
                "Please wait until you get redirected to your project details, you can customize your survey there"
              )}
            />
          </Container>
        ) : (
          <>
            <Typography variant="h6" ml="1rem" mb="2rem">
              {product.name}
            </Typography>
            {isDataLoaded ? (
              <SurveyBriefing
                locale={locale}
                json={{
                  ...briefing.json,
                  pages: [
                    ...(briefing.json?.pages ?? []),
                    {
                      elements: projectNamePage,
                    },
                  ],
                }}
                withRouteObserver={true}
                onComplete={handleSubmit}
                briefingSlug={slug}
                isBexioUser={isBexioUser}
                isBrandValuatorProduct={isBrandValuatorProduct}
                summaryClass={{
                  marginTop: "-3.2rem",
                }}
                insights={insights.length > 0 ? insights : []}
                orgCodename={params.codename}
              />
            ) : (
              <Loader />
            )}
          </>
        )}
      </Grid>
      <Modal
        isVisible={isModalVisible}
        onCloseClick={() => setIsModalVisible(!isModalVisible)}
        onBackdropClick={() => setIsModalVisible(!isModalVisible)}
      >
        <ImageBexio
          src={BexioDeeptrueIntegrationImage}
          alt="bexio deeptrue integration"
        />
        <BexioModalTitle>{t("bexioModal.title", "Welcome")}</BexioModalTitle>
        <BexioModalContent
          dangerouslySetInnerHTML={{
            __html: t(
              "bexioModal.content",
              "<p> We will help you to understand your customer better by sending a satisfaction survey whenever you receive a paid invoice from one of them. </p> <p> First let's find out what you need by filling out a quick brief. </p>"
            ),
          }}
        />
        <ModalActions>
          <Button
            id="start-brief"
            onClick={() => setIsModalVisible(!isModalVisible)}
          >
            {t("bexioModal.cta", "Start briefing")}
          </Button>
        </ModalActions>
      </Modal>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    briefing: selectors.products.briefing(state),
    product: selectors.products.selected(state),
    user: selectors.authSelectors.userProfile(state),
    isBexioUser: selectors.authSelectors.isBexioUser(state),
    isBrandValuatorProduct: selectors.products.isBrandValuatorProduct(state),
  }),
  {
    setProduct: actions.products.setProduct,
    clearBriefieng: actions.products.clearBriefing,
  }
)(Briefing);
